import psl from "psl";

import { swSettings } from "app/common/services/swSettings";
import {
    selectDashboardTemplate,
    templateAddKeys,
    templateSetParent,
} from "app/components/dashboard/dashboard-templates/actions/dashboardTemplateActions";
import {
    DashboardTemplateService,
    EDashboardParentType,
} from "app/components/dashboard/dashboard-templates/DashboardTemplateService";
import { getCountries } from "app/components/filters-bar/utils";
import {
    EnumWizardItems,
    setActivationWizardItems,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { BasicDurations } from "app/services/DurationService";
import { fetchSimilarSitesQuery } from "app/services/sitesService/queries/sitesQueries";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ThunkDispatchCommon } from "single-spa/store/types";
import { allTrackers } from "app/services/track/track";

export const FALLBACK_DOMAIN = "amazon.com";

export const navigateToDashboard = (dashboardId: string): void => {
    proNavigator.go("dashboard-exist", { dashboardId });
};

export const navigateToDashboardsTemplatesGallery = (): void => {
    setActivationWizardItems(EnumWizardItems.IsCreateDashboardFromHP, true);
    proNavigator.go("dashboard-gallery");
};

export const navigateToDashboardsDailyTrafficAnalysisTemplate = async (
    dispatch: ThunkDispatchCommon,
    domain?: string,
): Promise<void> => {
    allTrackers.dispatchTrackEvent(
        "Button",
        "click",
        "My Assets/Create a dashboard/Use template - Daily traffic analysis",
    );
    await DashboardTemplateService.generateDashboardTemplates();
    setActivationWizardItems(EnumWizardItems.IsCreateDashboardFromHP, true);
    proNavigator.go("dashboard-gallery").then(() => {
        dispatch(selectDashboardTemplate(6));
        dispatch(templateSetParent(EDashboardParentType.TEMPLATE, "6"));
        if (domain) {
            addTemplateKeys(dispatch, domain);
        }
    });
};

export const navigateToDashboardsKeywordsOptimizationTemplate = async (
    dispatch: ThunkDispatchCommon,
    domain?: string,
): Promise<void> => {
    allTrackers.dispatchTrackEvent(
        "Button",
        "click",
        "My Assets/Create a dashboard/Use template - keyword optimization",
    );
    await DashboardTemplateService.generateDashboardTemplates();
    setActivationWizardItems(EnumWizardItems.IsCreateDashboardFromHP, true);
    proNavigator.go("dashboard-gallery").then(() => {
        dispatch(selectDashboardTemplate(13));
        dispatch(templateSetParent(EDashboardParentType.TEMPLATE, "13"));
        if (domain) {
            addTemplateKeys(dispatch, domain);
        }
    });
};

export const navigateToKeywordGenerator = (params?): void => {
    proNavigator.go("digitalsuite_findkeywords_keywordGeneratorTool", { ...params });
};

export const navigateToKeywordAnalysisOverview = (keywordListId: string): void => {
    proNavigator.go("organicsearch_keywordAnalysis_overview_2", {
        keyword: `*${keywordListId}`,
        duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
        webSource: devicesTypes.DESKTOP,
        keywordsType: "both",
        country: getCountries()?.[0]?.id ?? WORLDWIDE_COUNTRY_ID,
    });
};

export const navigateToKeywordList = (keywordListId: string): void => {
    proNavigator.go("digitalsuite_monitorkeywords", {
        keywordGroupId: keywordListId,
        duration: BasicDurations.LAST_TWELVE_MONTHS,
        webSource: devicesTypes.TOTAL,
        keywordsType: "both",
        country: getCountries()?.[0]?.id ?? WORLDWIDE_COUNTRY_ID,
        keyword: `*${keywordListId}`,
    });
};

const getWebsiteAsset = async (domain: string): Promise<{ id: string; image: string }> => {
    const res = await getWebsiteResults(domain);
    const { name, image } = res[0];
    return { id: name, image };
};

export const addTemplateKeys = (dispatch, domain: string): void => {
    getWebsiteAsset(domain)
        .then(({ id, image }) => {
            const keys = [{ name: id, image }];
            fetchSimilarSitesQuery(domain, { limit: 3 })
                .then((res) => {
                    res.forEach(({ Domain, Favicon }) => keys.push({ name: Domain, image: Favicon }));
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => dispatch(templateAddKeys(keys)));
        })
        .catch((error) => {
            console.error(error);
        });
};

export const getUserWebsite = (): string | undefined => {
    if (swSettings.user?.customData?.quiz) {
        const rawUserWebsite = swSettings.user?.customData?.quiz?.website;
        const userWebsiteValid = rawUserWebsite && rawUserWebsite !== "no_website";
        return userWebsiteValid ? rawUserWebsite : undefined;
    } else {
        const email: string = swSettings.user?.username ?? "";
        const address = email.split("@").pop();
        return psl.isValid(address) ? psl.get(address) : undefined;
    }
};
